import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, TYPE_LABELS } from "../../lib/api";
import { useAuth } from "../../context/AppContext";
import { Loading } from "../../components/PageHeader";
import { Switch } from "../../components/ui/switch";
import { AdminTitle, ErrorBox } from "./AdminLayout";
import { ImagesField, FileSlot } from "./Uploaders";

const BLANK = {
  title: "", subtitle: "", description: "", type: "physical_art", price: "", images: [], preview_file: null,
  full_file: null, download_file: null, stock: 1, shipping_mode: "flat", shipping_my: 15, shipping_intl: 45,
  medium: "", dimensions: "", year: new Date().getFullYear(), duration: "", featured: false, active: true,
};

const Input = ({ label, k, f, set, type = "text", className = "", ...rest }) => (
  <div className={className}>
    <label className="wb-label">{label}</label>
    <input type={type} className="wb-input" value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`product-${k.replace(/_/g, "-")}-input`} {...rest} />
  </div>
);

function ShippingFields({ f, set }) {
  return (
    <div className="grid gap-5 sm:grid-cols-3">
      <Input label="Stock" k="stock" type="number" min="0" f={f} set={set} />
      <div>
        <label className="wb-label">Shipping mode</label>
        <select className="wb-input" value={f.shipping_mode} onChange={(e) => set("shipping_mode", e.target.value)} data-testid="product-shipping-mode-select">
          <option value="flat">Flat rate (Malaysia / International)</option>
          <option value="malaysia_only">Malaysia only</option>
          <option value="free">Free shipping</option>
        </select>
      </div>
      <div />
      {f.shipping_mode !== "free" && <Input label="Malaysia rate (USD)" k="shipping_my" type="number" step="0.01" min="0" f={f} set={set} />}
      {f.shipping_mode === "flat" && <Input label="International rate (USD)" k="shipping_intl" type="number" step="0.01" min="0" f={f} set={set} />}
    </div>
  );
}

function MediaFields({ f, set, meta, addMeta }) {
  const t = f.type;
  const av = t === "audio" ? "audio/*" : "video/*";
  return (
    <div className="grid gap-5 md:grid-cols-2">
      {(t === "audio" || t === "video") && <>
        <FileSlot label="Public preview clip (~30 sec)" kind="preview" accept={av} value={f.preview_file} meta={meta} onChange={(v) => set("preview_file", v)} onMeta={addMeta} hint="Anyone can play this." />
        <FileSlot label="Full file (buyers only)" kind="full" accept={av} value={f.full_file} meta={meta} onChange={(v) => set("full_file", v)} onMeta={addMeta} hint="Streamed in My Library and used for downloads."
          onGenerated={(m) => { addMeta(m); set("preview_file", m.id); }} />
      </>}
      {t === "digital_art" && (
        <FileSlot label="Downloadable file (buyers only)" kind="download" accept="image/*,application/pdf,application/zip" value={f.download_file} meta={meta} onChange={(v) => set("download_file", v)} onMeta={addMeta} hint="High-resolution file delivered via secure link." />
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="wb-card p-6 md:p-8"><div className="eyebrow mb-6">{title}</div>{children}</section>
);

export default function ProductForm() {
  const { id } = useParams();
  const { can } = useAuth();
  const canPrice = can("manage_prices");
  const nav = useNavigate();
  const [f, setF] = useState(id ? null : BLANK);
  const [meta, setMeta] = useState({});
  const [slug, setSlug] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!id) return;
    api.get(`/admin/products/${id}`).then(({ data }) => {
      setF({ ...BLANK, ...data, stock: data.stock ?? 1, shipping_mode: data.shipping_mode || "flat", year: data.year ?? "" });
      setMeta(data.files); setSlug(data.slug);
    }).catch((e) => setErr(errMsg(e)));
  }, [id]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const addMeta = (r) => setMeta((m) => ({ ...m, [r.id]: r }));
  const setImages = (fn) => setF((p) => ({ ...p, images: fn(p.images) }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const body = {
      ...f, price: Number(f.price), stock: Number(f.stock), shipping_my: Number(f.shipping_my || 0),
      shipping_intl: Number(f.shipping_intl || 0), year: f.year ? Number(f.year) : null,
    };
    try {
      await (id ? api.put(`/admin/products/${id}`, body) : api.post("/admin/products", body));
      toast.success(id ? "Product updated" : "Product created");
      nav("/admin/products");
    } catch (e2) {
      setErr(errMsg(e2)); toast.error(errMsg(e2));
    } finally { setBusy(false); }
  };

  if (err && !f) return <ErrorBox msg={err} />;
  if (!f) return <Loading />;
  return (
    <form onSubmit={save} className="space-y-8" data-testid="product-form">
      <AdminTitle eyebrow={id ? "Edit product" : "New product"} title={f.title || "Untitled work"}>
        {slug && f.active && <Link to={`/product/${slug}`} target="_blank" className="btn-ghost" data-testid="product-view-live-link">View live</Link>}
        <button type="submit" disabled={busy} className="btn-gold" data-testid="product-save-button">{busy ? "Saving…" : "Save product"}</button>
      </AdminTitle>
      {err && <ErrorBox msg={err} />}
      <Section title="Basics">
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label className="wb-label">Type</label>
            <select className="wb-input" value={f.type} onChange={(e) => set("type", e.target.value)} data-testid="product-type-select">
              {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <Input label={canPrice ? "Price (USD)" : "Price (USD) · needs manage_prices"} k="price" type="number" step="0.01" min="0.01" required disabled={!canPrice} f={f} set={set} />
          <Input label="Title" k="title" required f={f} set={set} className="sm:col-span-2" />
          <Input label="Subtitle" k="subtitle" f={f} set={set} className="sm:col-span-2" />
          <div className="sm:col-span-2">
            <label className="wb-label">Description</label>
            <textarea rows={5} className="wb-input" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="product-description-input" />
          </div>
          <Input label="Medium" k="medium" f={f} set={set} />
          <Input label={f.type === "physical_art" ? "Dimensions" : "Duration / format"} k={f.type === "physical_art" ? "dimensions" : "duration"} f={f} set={set} />
          <Input label="Year" k="year" type="number" f={f} set={set} />
        </div>
        <div className="mt-8 flex flex-wrap gap-10">
          <label className="flex items-center gap-3 text-sm"><Switch checked={f.active} onCheckedChange={(v) => set("active", v)} data-testid="product-active-switch" /> Published</label>
          <label className="flex items-center gap-3 text-sm"><Switch checked={f.featured} onCheckedChange={(v) => set("featured", v)} data-testid="product-featured-switch" /> Featured on home</label>
        </div>
      </Section>
      <Section title="Images"><ImagesField images={f.images} setImages={setImages} onMeta={addMeta} /></Section>
      {f.type === "physical_art"
        ? <Section title="Stock & shipping"><ShippingFields f={f} set={set} /></Section>
        : <Section title="Files"><MediaFields f={f} set={set} meta={meta} addMeta={addMeta} /></Section>}
    </form>
  );
}
